/**
 * chunkQueue.js — tiny IndexedDB-backed queue for audio chunks that couldn't
 * upload while offline. Enables "finish the session in the elevator" resilience.
 *
 * Schema: DB `pura_offline`, store `audio_chunks`.
 * Record:
 *   {
 *     id,           // uuid-ish string, primary key
 *     sessionId,    // which session this belongs to
 *     chunkIndex,   // the server-side chunk_index param
 *     blob,         // the audio Blob itself (stored as-is by IDB)
 *     ext,          // 'webm' | 'ogg'
 *     token,        // JWT at time of enqueue — good enough for short-lived drains
 *     createdAt,    // ms epoch
 *     attempts      // drain retry counter
 *   }
 */
const DB_NAME = 'pura_offline';
const DB_VERSION = 1;
const STORE = 'audio_chunks';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('sessionId', 'sessionId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function enqueueChunk(record) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').put({
      attempts: 0,
      createdAt: Date.now(),
      ...record,
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function listChunks(sessionId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = tx(db, 'readonly');
    const req = sessionId
      ? store.index('sessionId').getAll(sessionId)
      : store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function countChunks(sessionId) {
  const all = await listChunks(sessionId);
  return all.length;
}

export async function removeChunk(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function bumpAttempts(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const store = tx(db, 'readwrite');
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const rec = getReq.result;
      if (!rec) return resolve();
      rec.attempts = (rec.attempts || 0) + 1;
      const putReq = store.put(rec);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}
